import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2,
  AlertTriangle, CheckCircle, Clock, DollarSign,
  Lightbulb, Zap, CreditCard, Home, RefreshCw,
  ArrowUpRight, ArrowDownRight, Bell
} from "lucide-react";

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });

const statusStyles = {
  paid: { dot: "bg-emerald-500", text: "text-emerald-400", bg: "bg-emerald-500/10", label: "Paid" },
  overdue: { dot: "bg-red-500", text: "text-red-400", bg: "bg-red-500/10", label: "Overdue" },
  due_soon: { dot: "bg-amber-500", text: "text-amber-400", bg: "bg-amber-500/10", label: "Due Soon" },
  upcoming: { dot: "bg-blue-500", text: "text-blue-400", bg: "bg-blue-500/10", label: "Upcoming" },
};

const typeIcons = {
  recurring: RefreshCw,
  loan: DollarSign,
  credit_card: CreditCard,
  rent_income: ArrowUpRight,
  mortgage: Home,
};

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function FinancialCalendar() {
  const { api } = useAuth();
  const [calData, setCalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDialog, setShowDayDialog] = useState(false);
  const [aiReminders, setAiReminders] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/financial-calendar/events?year=${year}&month=${month}`);
      setCalData(res.data);
    } catch {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [api, year, month]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const goToPrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };

  const goToNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const goToToday = () => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
  };

  const openDay = (day) => {
    const events = calData?.day_map?.[String(day)] || [];
    if (events.length === 0) return;
    setSelectedDay({ day, events });
    setShowDayDialog(true);
  };

  const loadAiReminders = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/financial-calendar/ai-reminders");
      setAiReminders(res.data);
      toast.success("AI reminders generated");
    } catch {
      toast.error("Failed to generate AI reminders");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Financial Calendar">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  const summary = calData?.summary || {};
  const dayMap = calData?.day_map || {};
  const daysInMonth = calData?.days_in_month || 30;
  // Convert Monday=0 to Sunday-start: firstWeekday from Python is Mon=0
  const firstWeekday = calData?.first_weekday ?? 0; // 0=Mon in Python
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;
  const todayDay = isCurrentMonth ? today.getDate() : -1;

  // Build calendar grid (Mon start)
  const calendarCells = [];
  for (let i = 0; i < firstWeekday; i++) {
    calendarCells.push({ day: null });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const events = dayMap[String(d)] || [];
    calendarCells.push({ day: d, events });
  }

  return (
    <Layout title="Financial Calendar">
      <div className="space-y-6" data-testid="financial-calendar-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Financial Calendar</h1>
            <p className="text-sm text-zinc-500 mt-1">All bills, payments & income in one view</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={loadAiReminders} disabled={aiLoading} variant="outline" size="sm" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-sm" data-testid="ai-reminders-btn">
              {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Bell className="w-3.5 h-3.5 mr-1.5" />}
              AI Reminders
            </Button>
            <Button onClick={loadCalendar} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-sm" data-testid="refresh-calendar-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-total-events">
            <CardContent className="p-4">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Events</p>
              <p className="text-xl font-bold font-mono text-white mt-0.5">{summary.total_events || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-total-due">
            <CardContent className="p-4">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Bills Due</p>
              <p className="text-xl font-bold font-mono text-red-400 mt-0.5">{fmt(summary.total_due)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-income">
            <CardContent className="p-4">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Income Expected</p>
              <p className="text-xl font-bold font-mono text-emerald-400 mt-0.5">{fmt(summary.total_income)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-red-500/10 rounded-none" data-testid="stat-overdue">
            <CardContent className="p-4">
              <p className="text-[10px] text-red-400 uppercase tracking-wider">Overdue</p>
              <p className="text-xl font-bold font-mono text-red-400 mt-0.5">{summary.overdue_count || 0}</p>
              {summary.overdue_amount > 0 && <p className="text-[10px] text-red-400/60">{fmt(summary.overdue_amount)}</p>}
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-amber-500/10 rounded-none" data-testid="stat-due-soon">
            <CardContent className="p-4">
              <p className="text-[10px] text-amber-400 uppercase tracking-wider">Due Soon</p>
              <p className="text-xl font-bold font-mono text-amber-400 mt-0.5">{summary.due_soon_count || 0}</p>
              {summary.due_soon_amount > 0 && <p className="text-[10px] text-amber-400/60">{fmt(summary.due_soon_amount)}</p>}
            </CardContent>
          </Card>
        </div>

        {/* Month Navigation */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <Button onClick={goToPrevMonth} variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="prev-month-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-white" data-testid="month-title">
                  {calData?.month_name} {year}
                </h2>
                {!isCurrentMonth && (
                  <Button onClick={goToToday} variant="outline" size="sm" className="text-xs border-white/10 text-zinc-400 hover:text-white rounded-sm h-6 px-2" data-testid="today-btn">
                    Today
                  </Button>
                )}
              </div>
              <Button onClick={goToNextMonth} variant="ghost" size="sm" className="text-zinc-400 hover:text-white" data-testid="next-month-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAYS.map((d) => (
                <div key={d} className="text-center text-[10px] text-zinc-600 uppercase tracking-wider py-1">
                  {d}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
              {calendarCells.map((cell, i) => {
                if (!cell.day) {
                  return <div key={`empty-${i}`} className="min-h-[80px] bg-white/[0.01]" />;
                }
                const isToday = cell.day === todayDay;
                const hasEvents = cell.events.length > 0;
                const hasOverdue = cell.events.some((e) => e.status === "overdue");
                const hasDueSoon = cell.events.some((e) => e.status === "due_soon");
                const hasIncome = cell.events.some((e) => e.type === "rent_income");
                const totalAmount = cell.events.reduce((s, e) => s + (e.type !== "rent_income" ? e.amount : 0), 0);

                return (
                  <div
                    key={cell.day}
                    onClick={() => openDay(cell.day)}
                    className={`min-h-[80px] p-1.5 border transition-colors ${
                      isToday ? "border-blue-500/40 bg-blue-500/5" :
                      hasOverdue ? "border-red-500/20 bg-red-500/[0.03]" :
                      hasDueSoon ? "border-amber-500/20 bg-amber-500/[0.03]" :
                      hasEvents ? "border-white/10 bg-white/[0.02]" :
                      "border-white/5 bg-white/[0.01]"
                    } ${hasEvents ? "cursor-pointer hover:bg-white/[0.04]" : ""}`}
                    data-testid={`day-${cell.day}`}
                  >
                    {/* Day Number */}
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-mono ${isToday ? "text-blue-400 font-bold" : "text-zinc-500"}`}>
                        {cell.day}
                      </span>
                      {hasEvents && (
                        <span className="text-[9px] text-zinc-600">{cell.events.length}</span>
                      )}
                    </div>

                    {/* Event Dots */}
                    <div className="space-y-0.5">
                      {cell.events.slice(0, 3).map((e) => {
                        const s = statusStyles[e.status] || statusStyles.upcoming;
                        return (
                          <div key={e.id} className="flex items-center gap-1">
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
                            <span className="text-[9px] text-zinc-400 truncate">{e.title.length > 12 ? e.title.slice(0, 12) + "..." : e.title}</span>
                          </div>
                        );
                      })}
                      {cell.events.length > 3 && (
                        <span className="text-[9px] text-zinc-600">+{cell.events.length - 3} more</span>
                      )}
                    </div>

                    {/* Total for day */}
                    {totalAmount > 0 && (
                      <div className="mt-auto pt-0.5">
                        <span className="text-[9px] font-mono text-zinc-600">{fmt(totalAmount)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/5">
              {Object.entries(statusStyles).map(([key, s]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${s.dot}`} />
                  <span className="text-[10px] text-zinc-500 capitalize">{s.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events List */}
        {calData?.events?.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-blue-400" /> All Events This Month
                <span className="text-xs text-zinc-600 font-normal ml-auto">{calData.events.length} events</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5" data-testid="events-list">
                {calData.events.map((e) => {
                  const s = statusStyles[e.status] || statusStyles.upcoming;
                  const Icon = typeIcons[e.type] || DollarSign;
                  const isIncome = e.type === "rent_income";
                  return (
                    <div key={e.id} className={`flex items-center gap-3 p-2.5 rounded-sm border ${
                      e.status === "overdue" ? "border-red-500/10 bg-red-500/[0.03]" :
                      e.status === "due_soon" ? "border-amber-500/10 bg-amber-500/[0.03]" :
                      "border-white/5 bg-white/[0.02]"
                    }`} data-testid={`event-${e.id}`}>
                      <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${s.bg}`}>
                        <Icon className={`w-4 h-4 ${s.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white truncate">{e.title}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${s.bg} ${s.text}`}>{s.label}</span>
                        </div>
                        <span className="text-[10px] text-zinc-600">{e.date} - {e.category}</span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-sm font-mono ${isIncome ? "text-emerald-400" : s.text}`}>
                          {isIncome ? "+" : "-"}{fmt(e.amount)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Reminders */}
        {aiReminders && (
          <Card className="bg-[#0A0A0A] border-cyan-500/10 rounded-none" data-testid="ai-reminders-section">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Bell className="w-4 h-4 text-cyan-400" /> AI Smart Reminders
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {aiReminders.monthly_outlook && (
                <p className="text-sm text-zinc-300 leading-relaxed">{aiReminders.monthly_outlook}</p>
              )}

              {aiReminders.urgent_alerts?.length > 0 && (
                <div>
                  <p className="text-xs text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Urgent
                  </p>
                  <div className="space-y-1.5">
                    {aiReminders.urgent_alerts.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-red-300 bg-red-500/5 border border-red-500/10 p-2.5 rounded-sm">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {a}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiReminders.this_week?.length > 0 && (
                <div>
                  <p className="text-xs text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> This Week
                  </p>
                  <div className="space-y-1.5">
                    {aiReminders.this_week.map((t, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Clock className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" /> {t}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiReminders.upcoming_large?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Large Upcoming Payments</p>
                  <div className="space-y-2">
                    {aiReminders.upcoming_large.map((item, i) => (
                      <div key={i} className="bg-white/[0.02] border border-white/5 p-3 rounded-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white font-medium">{item.title}</span>
                          <span className="text-xs text-red-400 font-mono">{fmt(item.amount)}</span>
                        </div>
                        <p className="text-xs text-zinc-500">{item.date}</p>
                        {item.tip && <p className="text-xs text-zinc-400 mt-1">{item.tip}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiReminders.cash_flow_warning && (
                <div className="bg-amber-500/5 border border-amber-500/10 p-3 rounded-sm">
                  <p className="text-xs text-amber-400 uppercase tracking-wider mb-1">Cash Flow Warning</p>
                  <p className="text-sm text-amber-300">{aiReminders.cash_flow_warning}</p>
                </div>
              )}

              {aiReminders.optimization_tips?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Tips</p>
                  <div className="space-y-1.5">
                    {aiReminders.optimization_tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Zap className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" /> {tip}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Day Detail Dialog */}
        <Dialog open={showDayDialog} onOpenChange={setShowDayDialog}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-md" data-testid="day-detail-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">
                {selectedDay && `${calData?.month_name} ${selectedDay.day}, ${year}`}
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">
                {selectedDay?.events.length} financial event{selectedDay?.events.length !== 1 ? "s" : ""}
              </DialogDescription>
            </DialogHeader>
            {selectedDay && (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {selectedDay.events.map((e) => {
                  const s = statusStyles[e.status] || statusStyles.upcoming;
                  const Icon = typeIcons[e.type] || DollarSign;
                  const isIncome = e.type === "rent_income";
                  return (
                    <div key={e.id} className={`flex items-center gap-3 p-3 rounded-sm border ${s.bg} border-white/5`}>
                      <div className={`w-9 h-9 rounded flex items-center justify-center flex-shrink-0 bg-white/5`}>
                        <Icon className={`w-4.5 h-4.5 ${s.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{e.title}</p>
                        <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                          <span className={`${s.text}`}>{s.label}</span>
                          <span className="capitalize">{e.source}</span>
                        </div>
                        {e.extra && e.extra.balance !== undefined && (
                          <p className="text-[10px] text-zinc-600 mt-0.5">
                            Balance: {fmt(e.extra.balance)}
                            {e.extra.paid > 0 && ` (Paid: ${fmt(e.extra.paid)})`}
                          </p>
                        )}
                      </div>
                      <span className={`text-sm font-mono font-bold ${isIncome ? "text-emerald-400" : s.text}`}>
                        {isIncome ? "+" : ""}{fmt(e.amount)}
                      </span>
                    </div>
                  );
                })}
                <div className="pt-2 border-t border-white/5 flex items-center justify-between">
                  <span className="text-xs text-zinc-500">Day Total</span>
                  <span className="text-sm font-mono text-white font-bold">
                    {fmt(selectedDay.events.reduce((s, e) => s + e.amount, 0))}
                  </span>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
